import { api, ApiResponse, PaginatedResponse } from './api';

export interface ChangelogAuthor {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
}

export interface ChangelogItem {
  id: string;
  title: string;
  uuid: string;
}

export interface ChangelogEntry {
  id: string;
  uuid: string;
  workspace_id: string;
  item_id?: string;
  title: string;
  content: string;
  entry_type: 'new' | 'improved' | 'fixed' | 'removed' | 'security';
  version?: string;
  release_date?: string;
  author_id: string;
  is_published: boolean;
  published_at?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  author?: ChangelogAuthor;
  item?: ChangelogItem;
  items?: ChangelogItem[];
}

export interface CreateChangelogData {
  title: string;
  content: string;
  entry_type: 'new' | 'improved' | 'fixed' | 'removed' | 'security';
  version?: string;
  release_date?: string;
  item_id?: string;
  item_ids?: string[];
}

export interface UpdateChangelogData {
  title?: string;
  content?: string;
  entry_type?: 'new' | 'improved' | 'fixed' | 'removed' | 'security';
  version?: string;
  release_date?: string;
  item_id?: string;
  item_ids?: string[];
}

export const changelogApi = {
  getAll: async (workspaceId: string, publishedOnly?: boolean): Promise<ChangelogEntry[]> => {
    const params = new URLSearchParams({ per_page: '100' });
    if (publishedOnly) params.append('published_only', 'true');
    const response = await api.get<ApiResponse<PaginatedResponse<ChangelogEntry>>>(
      `/api/v1/workspaces/${workspaceId}/changelog?${params.toString()}`
    );
    return response.data.data?.data || [];
  },

  getPaginated: async (workspaceId: string, page = 1, perPage = 20, filters?: { published_only?: boolean; type?: string }): Promise<PaginatedResponse<ChangelogEntry>> => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (filters?.published_only) params.append('published_only', 'true');
    if (filters?.type) params.append('type', filters.type);
    const response = await api.get<ApiResponse<PaginatedResponse<ChangelogEntry>>>(
      `/api/v1/workspaces/${workspaceId}/changelog?${params.toString()}`
    );
    return response.data.data!;
  },

  getById: async (workspaceId: string, id: string): Promise<ChangelogEntry> => {
    const response = await api.get<ApiResponse<ChangelogEntry>>(
      `/api/v1/workspaces/${workspaceId}/changelog/${id}`
    );
    return response.data.data!;
  },

  create: async (workspaceId: string, data: CreateChangelogData): Promise<ChangelogEntry> => {
    const response = await api.post<ApiResponse<ChangelogEntry>>(
      `/api/v1/workspaces/${workspaceId}/changelog`,
      data
    );
    return response.data.data!;
  },

  update: async (workspaceId: string, id: string, data: UpdateChangelogData): Promise<ChangelogEntry> => {
    const response = await api.patch<ApiResponse<ChangelogEntry>>(
      `/api/v1/workspaces/${workspaceId}/changelog/${id}`,
      data
    );
    return response.data.data!;
  },

  publish: async (workspaceId: string, id: string): Promise<ChangelogEntry> => {
    const response = await api.post<ApiResponse<ChangelogEntry>>(
      `/api/v1/workspaces/${workspaceId}/changelog/${id}/publish`
    );
    return response.data.data!;
  },

  delete: async (workspaceId: string, id: string): Promise<void> => {
    await api.delete(`/api/v1/workspaces/${workspaceId}/changelog/${id}`);
  },
};

export const getTypeColor = (type: string): string => {
  switch (type) {
    case 'new': return '#10B981';
    case 'improved': return '#3B82F6';
    case 'fixed': return '#F59E0B';
    case 'removed': return '#EF4444';
    case 'security': return '#8B5CF6';
    default: return '#6B7280';
  }
};

export const getTypeLabel = (type: string): string => {
  switch (type) {
    case 'new': return 'New';
    case 'improved': return 'Improved';
    case 'fixed': return 'Fixed';
    case 'removed': return 'Removed';
    case 'security': return 'Security';
    default: return type;
  }
};
